import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import { trpc } from "@/lib/api/trpc";

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
}

interface UserSelectorProps {
  value: string | null;
  onChange: (userId: string | null) => void;
  disabled?: boolean;
  label?: string;
  helperText?: string;
}

export function UserSelector({
  value,
  onChange,
  disabled = false,
  label = "Select User",
  helperText,
}: UserSelectorProps) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // @ts-ignore - tRPC type issues
  const { data, isLoading } = trpc.user.getUsers.useQuery(
    {
      page: 0,
      limit: 100,
      orderBy: "firstName",
      orderDirection: "asc",
      status: "ACTIVE", // Only show active users
    },
    {
      enabled: open, // Only fetch when dropdown is opened
    },
  );

  useEffect(() => {
    if (data?.users) {
      setOptions(data.users);
      
      // Set selected user if value is provided
      if (value) {
        const user = data.users.find((u: User) => u.id === value);
        if (user) {
          setSelectedUser(user);
        }
      }
    }
  }, [data, value]);

  const getUserDisplayName = (user: User) => {
    const fullName = `${user.firstName} ${user.lastName}`.trim();
    return fullName || user.email;
  };

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={selectedUser}
      onChange={(_, newValue) => {
        setSelectedUser(newValue);
        onChange(newValue?.id || null);
      }}
      options={options}
      getOptionLabel={(option) => getUserDisplayName(option)}
      isOptionEqualToValue={(option, value) => option.id === value?.id}
      loading={isLoading}
      disabled={disabled}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <Box>
            <Typography variant="body1">
              {getUserDisplayName(option)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {option.email} • {option.role}
            </Typography>
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}